/* eslint-disable no-nested-ternary */
/* eslint-disable no-unneeded-ternary */
/* eslint-disable no-unused-vars */
/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useRef, useCallback } from "react";

// react-router components
import { useLocation, Link, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Breadcrumbs from "pages/Breadcrumbs";
import NotificationItem from "pages/Items/NotificationItem";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "pages/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import ArticleIcon from "@mui/icons-material/Article";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { generateAppetitePdf } from "pages/Anticipate/homeAppetitePdf";
import { useMsal } from "@azure/msal-react";
import Taxonomy from "pages/Taxonomy";
import { Stack, MenuItem } from "@mui/material";
import { siteData, protectedResources } from "authConfig";
import useFetchWithMsal from "hooks/useFetchWithMsal";
import { useCurrentUser } from "context/UserContext";
import { ReactComponent as NotificationIcon } from "../../../assets/images/icons/projectIcons/icon-social-notifications_24px.svg";
import { ReactComponent as SettingsIcon } from "../../../assets/images/icons/projectIcons/icon-action-settings_24px.svg";
import Ramsay from "../../../assets/images/icons/projectIcons/Ramsay.png";
import Orica from "../../../assets/images/icons/projectIcons/orica.png";
import { ReactComponent as LogoutIcon } from "../../../assets/images/icons/projectIcons/icon-navigation-expand_more_24px.svg";

function getGreetingName(user, profileUserName, activeAccount) {
  if (profileUserName && !String(profileUserName).includes("@")) return profileUserName;
  if (user?.firstName) return user.firstName;
  const name = user?.name || activeAccount?.idTokenClaims?.name || activeAccount?.name || "";
  if (name && !String(name).includes("@")) return String(name).trim().split(/\s+/)[0] || "";
  if (activeAccount?.idTokenClaims?.given_name) return activeAccount.idTokenClaims.given_name;
  return "";
}

function DashboardNavbar({ absolute, light, isMini }) {
  const { instance } = useMsal();
  const { user, role } = useCurrentUser();
  const { execute } = useFetchWithMsal({
    scopes: Array.isArray(siteData?.apiScopes)
      ? siteData.apiScopes
      : siteData?.apiScopes
      ? [siteData.apiScopes]
      : [],
  });
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const navigate = useNavigate();
  const apiBase = protectedResources.apiTodoList.endpoint;

  // "View Risk Appetite" (global): fetch the taxonomy + appetite config on click, then generate the
  // PAGER-branded risk-appetite PDF. Available on every screen from the top nav.
  const openAppetitePdf = useCallback(() => {
    const run = executeRef.current;
    Promise.all([
      run("GET", `${apiBase}/taxonomy/list`).catch(() => null),
      run("GET", `${apiBase}/appetite-levels/config`).catch(() => null),
    ]).then(([taxRes, appRes]) => {
      let taxList = [];
      if (Array.isArray(taxRes?.data)) taxList = taxRes.data;
      else if (Array.isArray(taxRes)) taxList = taxRes;
      const taxDoc = taxList.find((t) => t.status === "published") || taxList[0] || null;
      const categories = (taxDoc?.Categories || []).map((c) => ({
        name: c.name || "",
        desc: c.desc || "",
        owner: c.Owner || "",
        SubCategories: (c.SubCategories || []).map((s) => ({
          name: s.name || "",
          Appetite: s.Appetite || "",
          Description: s.Description || "",
        })),
      }));
      const appCfg = appRes?.data || {};
      generateAppetitePdf({
        categories,
        appetite: {
          levels: appCfg.levels || [],
          criteria: appCfg.criteria || [],
          complianceStatement: appCfg.complianceStatement || "",
        },
        companyName: taxDoc?.name || "",
      });
    });
  }, [apiBase]);

  const [notifItems, setNotifItems] = useState([]);
  const [notifUnread, setNotifUnread] = useState(0);
  const [notifLoading, setNotifLoading] = useState(false);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(""); // uploaded avatar (ID30)

  const fetchNotifications = useCallback(
    (opts = { quiet: true }) => {
      if (!apiBase) return Promise.resolve();
      if (!opts.quiet) setNotifLoading(true);
      const run = executeRef.current("GET", `${apiBase}/notifications?limit=25`);
      return Promise.resolve(run)
        .then((res) => {
          if (res && res.success === true && res.data && typeof res.data === "object") {
            setNotifItems(Array.isArray(res.data.items) ? res.data.items : []);
            setNotifUnread(typeof res.data.unreadCount === "number" ? res.data.unreadCount : 0);
          }
        })
        .finally(() => {
          if (!opts.quiet) setNotifLoading(false);
        });
    },
    [apiBase]
  );

  useEffect(() => {
    fetchNotifications({ quiet: true });
  }, [fetchNotifications]);

  // ID30 — load the user's uploaded profile photo for the top-right avatar (dummy if none).
  useEffect(() => {
    if (!apiBase) return;
    Promise.resolve(executeRef.current("GET", `${apiBase}/profilesetting/list`))
      .then((res) => {
        if (res?.success === true && Array.isArray(res.data) && res.data[0]?.userPhoto) {
          setProfilePhotoUrl(res.data[0].userPhoto);
        }
      })
      .catch(() => {});
  }, [apiBase]);

  const openNotificationTarget = useCallback(
    (link) => {
      const u = (link || "").trim();
      if (u.startsWith("http://") || u.startsWith("https://")) {
        try {
          const url = new URL(u);
          if (url.origin === window.location.origin) {
            navigate(`${url.pathname}${url.search}${url.hash}`);
            return;
          }
        } catch {
          /* ignore */
        }
        window.open(u, "_blank", "noopener,noreferrer");
        return;
      }
      navigate("/anticipate/HorizonScan");
    },
    [navigate]
  );

  const activeAccount = instance.getActiveAccount();
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [logoutOpenMenu, setLogoutOpenMenu] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);

  const [userName, setUserName] = useState("");
  const profileSettingFetched = useRef(false);

  useEffect(() => {
    if (profileSettingFetched.current) return;
    profileSettingFetched.current = true;
    executeRef
      .current("GET", `${protectedResources.apiTodoList.endpoint}/profilesetting/list`)
      .then((response) => {
        if (response && response.success === true && response.data.length > 0) {
          setUserName(response.data[0].userName);
        }
      });
  }, []);

  const tenantName = user?.tenantName || user?.tenantId || "";

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  // The top-right settings icon must always OPEN settings (show the settings menu + Taxonomy),
  // never toggle it off. Toggling caused a second click to revert the left menu to the normal one
  // while still navigating to Taxonomy (defect 57). "Exit Settings" in the sidenav handles closing.
  const handleOpenSettings = () => setOpenConfigurator(dispatch, true);
  const handleOpenMenu = (event) => {
    setOpenMenu(event.currentTarget);
    fetchNotifications({ quiet: false });
  };
  const handleCloseMenu = () => setOpenMenu(false);
  const handleLogoutOpenMenu = (event) => setLogoutOpenMenu(event.currentTarget);
  const handleLogoutCloseMenu = () => setLogoutOpenMenu(false);
  // Render the notifications menu — only unread alerts show; reading one removes it (ID58).
  const unreadItems = notifItems.filter((n) => !n.readAt);
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2, maxHeight: 360 }}
    >
      {notifLoading ? (
        <MenuItem disabled>Loading notifications…</MenuItem>
      ) : unreadItems.length === 0 ? (
        <MenuItem disabled>No alerts</MenuItem>
      ) : (
        [
          notifUnread > 0 ? (
            <MenuItem
              key="mark-all-read"
              onClick={() => {
                execute("POST", `${apiBase}/notifications/read-all`, {}).then((res) => {
                  if (res && res.success) fetchNotifications({ quiet: true });
                });
                handleCloseMenu();
              }}
              dense
              sx={{ borderBottom: 1, borderColor: "divider", fontWeight: 600 }}
            >
              Mark all read
            </MenuItem>
          ) : null,
          ...unreadItems.map((n) => (
            <NotificationItem
              key={n.id}
              icon={<Icon fontSize="small">priority_high</Icon>}
              title={n.title}
              onClick={() => {
                handleCloseMenu();
                const unread = !n.readAt;
                if (unread) {
                  execute("PATCH", `${apiBase}/notifications/${n.id}/read`, {}).then((res) => {
                    if (res && res.success) fetchNotifications({ quiet: true });
                  });
                }
                openNotificationTarget(n.link);
              }}
            />
          )),
        ]
      )}
    </Menu>
  );
  const renderLogoutMenu = () => (
    <Menu
      anchorEl={logoutOpenMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(logoutOpenMenu)}
      onClose={handleLogoutCloseMenu}
      sx={{ mt: 2 }}
    >
      <Link to="/authentication/logout">
        <NotificationItem icon={<Icon>logout</Icon>} title="Logout" />
      </Link>
    </Menu>
  );
  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  const handleNavigate = () => {
    navigate("/Taxonomy");
  };
  // const titleWithSpaces = decodeURIComponent(route[route.length - 1]);
  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
      // sx={{ p: "2px" }}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        {/* <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={titleWithSpaces} route={route} light={light} />
        </MDBox> */}
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          {/* <Breadcrumbs icon="home" title={titleWithSpaces} route={route} light={light} /> */}
          <Stack direction="column">
            <span style={{ fontSize: "25px", color: "#263445", lineHeight: 1 }}>
              {route[0] === "home" && "Home"} {route[0] === "overview" && "Protect"}
              {route[0] === "check" && "Check"}
              {route[0] === "Taxonomy" && "Configuration - Taxonomy"}
              {route[0] === "AppetiteSetting" && "Configuration - Protect"}
              {route[0] === "ConfigureSizingCriteria" && "Configuration - Protect"}
              {route[0] === "AssuranceSizingCriteria" && "Configuration - Protect"}
              {route[0] === "AssuranceProportionality" && "Configuration - Protect"}
              {route[0] === "LikelihoodCriteria" && "Configuration - Protect"}
            </span>
            <span style={{ fontSize: "17px", color: "#8090AA", paddingLeft: "2px" }}>
              {route[0] === "home" && "Your Risk Snapshot"}
              {route[0] === "check" &&
                "Insights to proactively detect trends and improve management of risks"}
              {route[0] === "overview" && "Internal intelligence to protect existing value"}
              {route[0] === "Taxonomy" && "Configure Taxonomy"}
              {route[0] === "AppetiteSetting" && "Protect Settings"}
              {route[0] === "ConfigureSizingCriteria" && "Protect Settings"}
              {route[0] === "AssuranceSizingCriteria" && "Protect Settings"}
              {route[0] === "AssuranceProportionality" && "Protect Settings"}
              {route[0] === "LikelihoodCriteria" && "Protect Settings"}
            </span>
          </Stack>
        </MDBox>
        {!isMini && (
          <MDButton
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={openAppetitePdf}
            sx={{
              color: "#52009b !important",
              borderColor: "#52009b",
              ml: "auto",
              mr: 1,
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              "&:hover": { borderColor: "#3a0070", bgcolor: "rgba(82,0,155,0.04)" },
            }}
          >
            View Risk Appetite
          </MDButton>
        )}
        {!isMini && role !== "VIEW" && (
          <MDButton
            variant="contained"
            startIcon={<ArticleIcon />}
            onClick={() => navigate("/anticipate/risk-advisor?mode=report")}
            sx={{
              bgcolor: "#52009b",
              color: "#fff !important",
              mr: 2,
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              "&:hover": { bgcolor: "#3a0070" },
            }}
          >
            Generate Report
          </MDButton>
        )}
        {isMini ? null : (
          // <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
          <MDBox
            sx={{
              backgroundColor: "white !important",
              borderRadius: "35px",
              // paddingRight: "0px !important",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              padding: "8px",

              justifyContent: "space-around",
            }}
            width="400px"
            height="60px"
            backgroundColor={light ? "white" : "inherit"}
          >
            <MDTypography fontSize="15px">{tenantName || "Company Name"}</MDTypography>

            <MDBox color={light ? "white" : "inherit"} sx={{ display: "flex" }}>
              {/* <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={() => {
                  handleConfiguratorOpen();
                  handleNavigate();
                }}
                route="/Taxonomy"
              >
                <Icon sx={iconsStyle}>settings</Icon>
              </IconButton> */}
              <Box
                component="span"
                sx={{ display: "inline-flex", cursor: "pointer", alignItems: "center" }}
                onClick={handleOpenMenu}
              >
                <Badge
                  color="error"
                  invisible={notifUnread === 0}
                  badgeContent={notifUnread > 99 ? "99+" : notifUnread}
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: 10,
                      minWidth: 18,
                      height: 18,
                      right: 4,
                      top: 4,
                    },
                  }}
                >
                  <NotificationIcon style={{ cursor: "pointer", display: "block" }} />
                </Badge>
              </Box>
              {renderMenu()}
              {role !== "VIEW" && (
                <SettingsIcon
                  onClick={() => {
                    handleOpenSettings();
                    handleNavigate();
                  }}
                  route="/Taxonomy"
                  style={{ cursor: "pointer" }}
                />
              )}

              {/* <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton> */}

              {/* <IconButton
                size="small"
                disableRipple
                color="black"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <Icon sx={iconsStyle}>notifications</Icon>
              </IconButton> */}

              {/* <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={handleLogoutOpenMenu}
              >
                <Icon sx={iconsStyle}>account_circle</Icon>
              </IconButton> */}
            </MDBox>
            <MDTypography fontSize="15px">
              {"Hi "}
              {getGreetingName(user, userName, activeAccount) || "there"}
              {" !"}
            </MDTypography>
            {/* Top-right avatar — uploaded photo, or a dummy avatar when none (ID30) */}
            <button
              type="button"
              aria-label="Open profile"
              onClick={() => navigate("/dashboard/Profile")}
              style={{
                padding: 0,
                border: "none",
                background: "none",
                cursor: "pointer",
                lineHeight: 0,
              }}
            >
              {profilePhotoUrl ? (
                <img
                  alt="profile"
                  src={profilePhotoUrl}
                  // Fall back to the generic icon if the uploaded image fails to load.
                  onError={() => setProfilePhotoUrl("")}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <AccountCircleIcon
                  sx={{
                    fontSize: "48px !important",
                    width: "48px !important",
                    height: "48px !important",
                    color: "#9e9e9e",
                    display: "block",
                  }}
                />
              )}
            </button>
            <LogoutIcon onClick={handleLogoutOpenMenu} />
            {renderLogoutMenu()}
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
